import type { SopReplayView } from '@erp/rpc';
import type { Span, SpanStatus } from './types';
import { UNASSIGNED_PARENT } from './liveActionSpans';
import { sopProcedureSpanId, sopStepSpanId } from './utils';

// ---------------------------------------------------------------------------
// Procedure rows for a replay that is still RUNNING.
//
// The persisted timeline can't show these yet. A replayed step's audit rows are written when the
// operator's tool call resolves, so for the whole live replay — which is minutes, not seconds — the
// trace is empty while the desktop is visibly working. The AG-UI stream knows the steps as they
// happen; this turns them into the same rows `groupSopReplaySpans` will later build from the
// timeline, keyed identically, so the persisted versions replace them rather than duplicate them.
//
// The individual actions come from `liveActionSpans`, attributed by arrival order rather than by
// any recorded link — see that module for exactly how much of a guess that is.
// ---------------------------------------------------------------------------

interface ProcedureRef {
  name: string;
  ordinal: number;
}

type ReplayStep =
  SopReplayView['steps'][number] | NonNullable<SopReplayView['running']>;

/**
 * The live replay of the operator call in flight, with the owner its rows are keyed by.
 *
 * The stream names no owner of its own, and the session can't stand in for one: a task loop logs
 * in once and every task's operator call replays on that one session, whose buffered events hold
 * every replay it has run — each opening at ordinal 1. So both are resolved here, once, for every
 * live consumer: the rows belong to the operator call still running in the persisted tree, and
 * only the steps since the replay last started over are its own.
 */
export interface LiveSopReplay {
  replay: SopReplayView;
  /** The running operator call's tool-call id — see `sopProcedureSpanId`. */
  owner: string | null;
}

/** An operator call: a subagent tool call with the id its replay rows record as `parentToolCallId`. */
function isOperatorCall(span: Span): boolean {
  return Boolean(span.isSubagent && !span.isSopProcedure && span.toolCallId);
}

function hasOperatorCall(spans: Span[]): boolean {
  return spans.some(
    (span) => isOperatorCall(span) || hasOperatorCall(span.children ?? []),
  );
}

/** The operator call in flight: the latest-started running operator call. */
function runningOperatorCall(spans: Span[]): Span | null {
  let found: Span | null = null;
  for (const span of spans) {
    const below = span.children ? runningOperatorCall(span.children) : null;
    for (const candidate of [span, below]) {
      if (
        candidate &&
        isOperatorCall(candidate) &&
        candidate.status === 'running' &&
        (!found || candidate.startMs >= found.startMs)
      ) {
        found = candidate;
      }
    }
  }
  return found;
}

/** Does `next` begin a new replay rather than continue `prev`'s? Within one operator call the
 * ordinal only grows, and a procedure's steps only move forward. */
function startsOver(prev: ReplayStep, next: ReplayStep): boolean {
  const before = (prev.procedure ?? UNKNOWN_PROCEDURE).ordinal;
  const after = (next.procedure ?? UNKNOWN_PROCEDURE).ordinal;
  return after < before || (after === before && next.index <= prev.index);
}

/** The latest replay's steps alone; anything earlier belongs to a call that has already persisted. */
function currentReplay(replay: SopReplayView): SopReplayView {
  const { steps, running } = replay;
  let start = 0;
  for (let i = 1; i < steps.length; i++) {
    if (startsOver(steps[i - 1], steps[i])) start = i;
  }
  const last = steps.at(-1);
  if (running && last && startsOver(last, running)) start = steps.length;
  return start === 0 ? replay : { ...replay, steps: steps.slice(start) };
}

function hasProcedure(span: Span): boolean {
  return (span.children ?? []).some((c) => c.isSopProcedure || hasProcedure(c));
}

/** Pair the stream's replay with the owner it has in the persisted tree it will be merged into. */
export function resolveLiveSopReplay(
  tree: Span[],
  replay: SopReplayView | null | undefined,
): LiveSopReplay | null {
  if (!replay) return null;
  const call = runningOperatorCall(tree);
  // The tree has operator calls but none still running: the buffer is a finished call's, whose own
  // rows have persisted under its id. Keyed under no owner, the stream's copy would not meet them
  // and would render as a second procedure. Ownerless rows are only safe while the tree has no
  // operator call at all — the stream is then simply ahead of the first poll.
  if (!call && hasOperatorCall(tree)) return null;
  const current = currentReplay(replay);
  // A call that has neither a step in flight nor a persisted procedure hasn't begun replaying, so
  // the settled steps still in the buffer are the PREVIOUS call's — keyed under this one they would
  // pass for its own. Holding back costs at most a poll of placeholders; misfiling them costs more.
  const notBegun = call && !current.running && !hasProcedure(call);
  return {
    replay: notBegun ? { ...current, steps: [] } : current,
    owner: call?.toolCallId ?? null,
  };
}

/** A live step's row id, under the procedure it names (the first, when it names none). */
export function liveSopStepSpanId(
  owner: string | null,
  step: ReplayStep,
): string {
  return sopStepSpanId(
    owner,
    (step.procedure ?? UNKNOWN_PROCEDURE).ordinal,
    step,
  );
}

/**
 * Where streamed actions have been filed, kept by the caller across renders.
 *
 * `groupLiveActions` records each action's parent step once and never moves it. But a parent
 * recorded before the poll had loaded the operator call is keyed under no owner, and once the owner
 * is known that step's row is keyed under it — so the action would be filed under an id no row has,
 * and vanish until its persisted twin lands. `unowned` remembers those steps so the owner can be
 * filled in without moving the action to a different step.
 */
export interface LiveActionLedger {
  /** Streamed message key → the step-span id it was first seen under. */
  parentByMessage: Map<string, string>;
  /** Steps actions were filed under before their owner was known, by that ownerless id. */
  unowned: Map<string, ReplayStep>;
}

export function newLiveActionLedger(): LiveActionLedger {
  return { parentByMessage: new Map(), unowned: new Map() };
}

/**
 * The step-span id to file newly seen actions under: the step running now, else
 * `UNASSIGNED_PARENT`. Once the owner is known it also re-keys the actions filed without one — they
 * can only be its own, since `resolveLiveSopReplay` leaves the owner unknown only while the tree
 * has no operator call at all.
 */
export function liveActionParent(
  live: LiveSopReplay | null,
  ledger: LiveActionLedger,
): string {
  const owner = live?.owner ?? null;
  if (owner) adoptOwner(ledger, owner);
  const running = live?.replay.running;
  if (!running) return UNASSIGNED_PARENT;
  const id = liveSopStepSpanId(owner, running);
  if (!owner) ledger.unowned.set(id, running);
  return id;
}

function adoptOwner(ledger: LiveActionLedger, owner: string): void {
  if (ledger.unowned.size === 0) return;
  for (const [message, parent] of ledger.parentByMessage) {
    const step = ledger.unowned.get(parent);
    if (step) {
      ledger.parentByMessage.set(message, liveSopStepSpanId(owner, step));
    }
  }
  ledger.unowned.clear();
}

// The run_sop path sends no procedure on the wire at all — only the ordinal matters for identity,
// so the empty name here is display-only and the row reads 'Procedure' until its persisted twin
// (which carries Node's fallback title) replaces it.
const UNKNOWN_PROCEDURE: ProcedureRef = { name: '', ordinal: 1 };

function stepStatus(mode: string | null): SpanStatus {
  return mode === 'failed' ? 'failed' : 'completed';
}

/** A procedure row to hang steps on, created once per ordinal. */
function procedureSpan(ref: ProcedureRef, owner: string | null): Span {
  return {
    id: sopProcedureSpanId(owner, ref.ordinal),
    type: 'tool',
    // Display-only: the run_sop path sends no name, so the row reads 'Procedure' until its
    // persisted twin (which carries Node's fallback title) replaces it.
    name: ref.name || 'Procedure',
    isSubagent: true,
    isSopProcedure: true,
    status: 'running',
    startMs: 0,
    durMs: null,
    children: [],
  };
}

/**
 * One replayed step as a row.
 *
 * `settled` separates the two callers: a step the stream has finished reporting carries its mode,
 * verify outcome and error, while the one still RUNNING has none of them yet — reading them off a
 * mid-flight step would render a mode badge and a verdict for work that hasn't happened.
 */
function stepSpan(
  step: ReplayStep,
  id: string,
  actionsByStep: ReadonlyMap<string, Span[]>,
  settled: boolean,
): Span {
  const base: Span = {
    id,
    type: 'tool',
    name: step.name,
    status: 'running',
    startMs: 0,
    durMs: null,
    sopStepIndex: step.index,
    children: actionsByStep.get(id) ?? [],
  };
  if (!settled) return base;
  const settledStep = step as SopReplayView['steps'][number];
  return {
    ...base,
    status: stepStatus(settledStep.mode),
    sopStepMode: settledStep.mode,
    sopStepNoop: settledStep.noop,
    sopVerify: settledStep.verify?.ran
      ? {
          kind: settledStep.verify.kind ?? '',
          passed: settledStep.verify.passed,
        }
      : null,
    stopReason: settledStep.error,
  };
}

/**
 * A procedure with a failed step failed; one still mid-flight stays running. Matches the rule
 * `groupSopReplaySpans` applies to the persisted rows.
 */
function settleProcedureStatus(procedure: Span): void {
  const children = procedure.children ?? [];
  if (children.some((c) => c.status === 'failed')) {
    procedure.status = 'failed';
  } else if (children.every((c) => c.status === 'completed')) {
    procedure.status = 'completed';
  } else {
    procedure.status = 'running';
  }
}

/** Synthetic procedure → step rows for whatever the stream has seen so far. */
export function liveSopSpans(
  { replay, owner }: LiveSopReplay,
  /** Streamed actions keyed by the step-span id they were seen under. */
  actionsByStep: ReadonlyMap<string, Span[]> = new Map(),
): Span[] {
  const procedures = new Map<string, Span>();

  function procedureFor(ref: ProcedureRef): Span {
    const id = sopProcedureSpanId(owner, ref.ordinal);
    const existing = procedures.get(id);
    if (existing) return existing;
    const span = procedureSpan(ref, owner);
    procedures.set(id, span);
    return span;
  }

  function add(step: ReplayStep, settled: boolean): void {
    procedureFor(step.procedure ?? UNKNOWN_PROCEDURE).children?.push(
      stepSpan(step, liveSopStepSpanId(owner, step), actionsByStep, settled),
    );
  }

  for (const step of replay.steps) add(step, true);

  // The running step only gets its own row if its settled version hasn't already landed.
  const running = replay.running;
  const settledIds = new Set(
    replay.steps.map((step) => liveSopStepSpanId(owner, step)),
  );
  if (running && !settledIds.has(liveSopStepSpanId(owner, running))) {
    add(running, false);
  }

  for (const procedure of procedures.values()) {
    settleProcedureStatus(procedure);
  }
  return [...procedures.values()];
}

function collectIds(spans: Span[], into: Set<string>): void {
  for (const span of spans) {
    into.add(span.id);
    if (span.children?.length) collectIds(span.children, into);
  }
}

/**
 * Add whatever the stream knows and the timeline doesn't.
 *
 * Persisted rows always win: they carry the real durations, screenshots and the operator's own
 * reasoning, none of which the stream's step view has. A live row is a placeholder that disappears
 * the moment its persisted twin lands — which works only because both sides derive their id the
 * same way (`sopStepSpanId`, under the owner `resolveLiveSopReplay` found in this same tree).
 */
export function mergeLiveSopSpans(
  tree: Span[],
  live: LiveSopReplay | null | undefined,
  actionsByStep: ReadonlyMap<string, Span[]> = new Map(),
): Span[] {
  const unassigned = actionsByStep.get(UNASSIGNED_PARENT) ?? [];
  if (!live || (live.replay.steps.length === 0 && !live.replay.running)) {
    // No procedure to hang them on — an ordinary operator run. Fall back to the row the actions
    // are plainly beneath: the deepest thing still running that has nothing under it yet.
    return unassigned.length > 0 ? attachToRunningLeaf(tree, unassigned) : tree;
  }

  const known = new Set<string>();
  collectIds(tree, known);

  const roots: Span[] = [];
  const grafts = new Map<string, Span[]>();
  for (const procedure of liveSopSpans(live, actionsByStep)) {
    const fresh = (procedure.children ?? []).filter((c) => !known.has(c.id));
    if (!known.has(procedure.id)) {
      roots.push({ ...procedure, children: fresh });
    } else if (fresh.length > 0) {
      // The procedure is already in the tree (its first steps persisted) but the later ones
      // haven't landed yet — those graft onto the existing row.
      grafts.set(procedure.id, fresh);
    }
  }

  const grafted = graft(tree, grafts);
  const merged = roots.length > 0 ? [...grafted, ...roots] : grafted;
  return unassigned.length > 0
    ? attachToRunningLeaf(merged, unassigned)
    : merged;
}

/**
 * Hang actions under the deepest running span that has no children yet.
 *
 * For a run with no procedure this is the operator's own tool call — the only row they could
 * sensibly belong to. Restricted to a CHILDLESS span on purpose: the moment that subtree has real
 * persisted rows, these placeholders stop being added rather than interleaving with them, since
 * nothing links a streamed action to its persisted twin. Losing the newest few for one poll beats
 * rendering every action twice.
 */
function attachToRunningLeaf(spans: Span[], actions: Span[]): Span[] {
  const target = deepestRunningLeaf(spans);
  if (!target) return spans;
  return graft(spans, new Map([[target, actions]]));
}

function deepestRunningLeaf(spans: Span[]): string | null {
  let found: string | null = null;
  const visit = (list: Span[]) => {
    for (const span of list) {
      if (span.children?.length) {
        visit(span.children);
        continue;
      }
      if (span.status === 'running') found = span.id;
    }
  };
  visit(spans);
  return found;
}

/**
 * Append each entry of `additions` to the span it is keyed by.
 *
 * Rebuilds only the branches that change and returns the input array itself when nothing does —
 * the tree is memoized upstream and re-rendered every poll, so mutating it in place would corrupt
 * the memo, and copying it wholesale would churn a new identity on every tick.
 */
function graft(spans: Span[], additions: Map<string, Span[]>): Span[] {
  if (additions.size === 0) return spans;
  let changed = false;
  const out = spans.map((span) => {
    const extra = additions.get(span.id);
    const children = span.children
      ? graft(span.children, additions)
      : undefined;
    if (!extra && children === span.children) return span;
    changed = true;
    const merged = [...(children ?? []), ...(extra ?? [])];
    merged.sort((a, b) => (a.sopStepIndex ?? 0) - (b.sopStepIndex ?? 0));
    const result = { ...span, children: merged };
    // A persisted prefix can be completed while the newly streamed steps are
    // still running or have failed. The parent must reflect the merged evidence.
    if (result.isSopProcedure) settleProcedureStatus(result);
    return result;
  });
  return changed ? out : spans;
}
